import { OverlayContainer, PopupContainer } from "@kalyzee/kast-web-sdk/web-components";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Debug } from "./helpers/debug";
import { initMediaDiagnostics } from "./helpers/mediaDiagnostics";
import reportWebVitals from "./reportWebVitals";

import { init as initUI } from "@kalyzee/kalyzee-ui";
import { init as initSDK } from "@kalyzee/kast-web-sdk";
import { AppAuthProvider } from "./components/auth/AppAuthProvider";
import { AppStoreProvider } from "./contexts/appStore/provider";

import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";

import "@kalyzee/kalyzee-ui/style.css";
import "@kalyzee/kalyzee-ui/themes.css";
import "@kalyzee/kast-web-sdk/web-components.css";

import "./index.css";

// important to import App after the global CSS
// So that WebPack will import the CSS modules after the global CSS
// thus giving them higher priority
import App from "./app/App";
import { AppThemeProvider } from "./components/theme/AppThemeProvider";
import { AnimatedBackgroundProvider } from "./components/utils/AnimatedBackground";
import { AppContextProvider } from "./contexts/app/context";
import { DeviceControlsProvider } from "./contexts/deviceControls/provider";
import { SdkContextProvider } from "./contexts/sdk/context";
import { SettingsBootGate } from "./components/utils/SettingsBootGate";

// Before anything can touch navigator.mediaDevices, so every call is observed.
initMediaDiagnostics();

initSDK({ lang: "fr" });
initUI({ lang: "fr" });

Debug.init(true);

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

// FOR DEBUG
// EN -> https://en.reactjs.org/docs/strict-mode.html
// FR -> https://fr.reactjs.org/docs/strict-mode.html
const ENABLE_STRICT_MODE = false;
const renderApp = () => (
  <AppThemeProvider>
    <AnimatedBackgroundProvider>
      <OverlayContainer />
      {/* Nothing below reads settings before they are resolved: a remote config
          is fetched first, so no provider mounts against stale content. */}
      <SettingsBootGate>
        <AppContextProvider>
          <SdkContextProvider>
            <DeviceControlsProvider>
              <AppAuthProvider>
                <AppStoreProvider>
                  <PopupContainer />
                  <BrowserRouter>
                      <App />
                  </BrowserRouter>
                  <ToastContainer
                    position="top-right"
                    pauseOnFocusLoss={false}
                    draggable={false}
                    pauseOnHover={false}
                    className="toast-container"
                  />
                </AppStoreProvider>
              </AppAuthProvider>
            </DeviceControlsProvider>
          </SdkContextProvider>
        </AppContextProvider>
      </SettingsBootGate>
    </AnimatedBackgroundProvider>
  </AppThemeProvider>
);

root.render(<Suspense fallback>{ENABLE_STRICT_MODE ? <React.StrictMode>{renderApp()}</React.StrictMode> : renderApp()}</Suspense>);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
